/**
 * Service for managing neural network models.
 */
import supabase from './supabaseClient';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

export interface Model {
  id: string;
  user_id: string;
  name: string;
  display_name?: string;
  description?: string;
  s3_base_path: string;
  architecture?: any;
  project_id?: string | null;
  model_type?: string;
  training_status?: string;
  created_at?: string;
  updated_at?: string;
}

export interface SaveModelRequest {
  name: string;
  architecture: any;
  project_id?: string;
  description?: string;
}

class ModelService {
  /**
   * Get authentication headers.
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  /**
   * List all models for the current user.
   */
  async listModels(projectId?: string): Promise<Model[]> {
    const headers = await this.getAuthHeaders();
    const url = projectId 
      ? `${BACKEND_URL}/api/storage/models?project_id=${projectId}`
      : `${BACKEND_URL}/api/storage/models`;

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list models');
    }

    const data = await response.json();
    console.log('[ModelService] Response:', data);
    return data.models || [];
  }

  /**
   * Get a specific model by ID.
   */
  async getModel(modelId: string): Promise<{ model: Model; architecture: any }> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/models/${modelId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get model');
    }

    return response.json();
  }

  /**
   * Save a new model.
   */
  async saveModel(request: SaveModelRequest): Promise<Model> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/models`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save model');
    }

    const data = await response.json();
    return data;
  }

  /**
   * Delete a model.
   */
  async deleteModel(modelId: string): Promise<void> {
    const headers = await this.getAuthHeaders();

    const response = await fetch(`${BACKEND_URL}/api/storage/models/${modelId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete model');
    }
  }
}

export const modelService = new ModelService();
export default modelService;

